import React, { useState } from 'react';
import { Download, FileText, File, Loader2 } from 'lucide-react';

interface ExportButtonsProps {
  onExportPDF: () => Promise<void>;
  onExportExcel: () => Promise<void>;
  variant?: 'buttons' | 'dropdown';
  disabled?: boolean;
}

export default function ExportButtons({
  onExportPDF,
  onExportExcel,
  variant = 'buttons',
  disabled = false,
}: ExportButtonsProps) {
  const [loading, setLoading] = useState<'pdf' | 'excel' | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleExport = async (type: 'pdf' | 'excel') => {
    setLoading(type);
    try {
      if (type === 'pdf') {
        await onExportPDF();
      } else {
        await onExportExcel();
      }
    } finally {
      setLoading(null);
      setShowDropdown(false);
    }
  };

  if (variant === 'dropdown') {
    return (
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={disabled || loading !== null}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          <span>Export</span>
        </button>

        {showDropdown && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
              <button
                onClick={() => handleExport('pdf')}
                disabled={loading !== null}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <FileText className="w-4 h-4" />
                <span>Export as PDF</span>
              </button>
              <button
                onClick={() => handleExport('excel')}
                disabled={loading !== null}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <File className="w-4 h-4" />
                <span>Export as Excel</span>
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <button
        onClick={() => handleExport('pdf')}
        disabled={disabled || loading !== null}
        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading === 'pdf' ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <FileText className="w-4 h-4" />
        )}
        <span>PDF</span>
      </button>
      <button
        onClick={() => handleExport('excel')}
        disabled={disabled || loading !== null}
        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading === 'excel' ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <File className="w-4 h-4" />
        )}
        <span>Excel</span>
      </button>
    </div>
  );
}
